import { FC } from 'react';
import AppSelectBox from 'components/FormElements/AppSelectBox';
import { styled } from '@mui/material';
import api from 'lib/trpcClient';

const Placeholder = styled('span')(() => ({
  color: '#999999',
}));

interface Props {
  firmId?: string;
  selectedCompanyId?: string;
  legalName?: string;
  onChange: (companyIds: Array<string>) => void;
  value: Array<string>;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  endDate?: Date;
  currentShareholdingsOnly?: boolean;
}

const ShareClassMultiSelect: FC<Props> = ({
  firmId,
  selectedCompanyId,
  legalName,
  onChange,
  value,
  endDate,
  currentShareholdingsOnly,
  disabled,
  ...rest
}) => {
  const { data: shareClasses, isFetching: isLoading } =
    api.investee.listShareClasses.useQuery(
      {
        investeeId: selectedCompanyId ?? '',
      },
      {
        enabled: !!selectedCompanyId,
        staleTime: 0,
        refetchOnWindowFocus: true,
      },
    );

  // Map share classes to the format expected by the component
  const filteredCompanies =
    shareClasses?.result
      .filter((shareClass) => {
        // If currentShareholdingsOnly is true, filter out share classes without allocation date
        if (currentShareholdingsOnly) {
          return !!shareClass.lastAllocationDate;
        }
        return true;
      })
      .map((shareClass) => {
        const shareClassName = shareClass.shareClass || 'Ordinary';
        // Only show last allocation text if there's actually a last allocation date
        const lastAllocationDate = shareClass.lastAllocationDate;
        const lastAllocationText = lastAllocationDate
          ? `(Last allocation: ${lastAllocationDate.toLabel('', 'dd/MM/yyyy')})`
          : '';

        return {
          _id: shareClass.investeeId,
          label: lastAllocationText
            ? `${shareClassName} ${lastAllocationText}`
            : shareClassName,
        };
      }) || [];

  const handleShareClassSelected = (selectedValue: string | Array<string>) => {
    const selectedArray = Array.isArray(selectedValue)
      ? selectedValue
      : [selectedValue];

    // Handle "all" option
    if (selectedArray.includes('all')) {
      if (value.length === filteredCompanies.length) {
        onChange([]);
      } else {
        onChange(filteredCompanies.map((company) => company._id));
      }
    } else {
      onChange(selectedArray.filter((id) => id !== 'all'));
    }
  };

  return (
    <AppSelectBox
      required
      data={filteredCompanies || []}
      valueKey="_id"
      labelKey="label"
      fullWidth
      value={value}
      variant="outlined"
      multiple
      disabled={isLoading || !filteredCompanies?.length || disabled}
      isCheckedAll={value.length === filteredCompanies.length}
      selectAllLabel="All share classes"
      //@ts-expect-error
      renderValue={(selected: Array<string>) => {
        if (isLoading) {
          return <Placeholder>Loading...</Placeholder>;
        } else if (!selectedCompanyId) {
          return <Placeholder>First select a company</Placeholder>;
        } else if (!filteredCompanies?.length) {
          return <Placeholder>No share classes found</Placeholder>;
        } else if (!selected || selected.length === 0) {
          return <Placeholder>Select share class</Placeholder>;
        } else if (selected.length === filteredCompanies.length) {
          return 'All share classes';
        } else if (selected.length === 1) {
          return (
            filteredCompanies.find((company) => company._id === selected[0])
              ?.label || ''
          );
        } else {
          return `${selected.length} selected`;
        }
      }}
      onChange={(e) => {
        const selectedValue = Array.isArray(e.target.value)
          ? e.target.value
          : [e.target.value];
        handleShareClassSelected(selectedValue);
      }}
      {...rest}
    />
  );
};

export default ShareClassMultiSelect;
